import React, { useEffect, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import { useLocation, useNavigate } from "react-router-dom";
import { AddButton } from "../../../../utils/Custom/Button";
import { submitSiteDelete } from "../../../../utils/commonComponent/delete";
import { fetchQuery } from "../../../../utils/commonComponent/fetched";
import ListSearchSection from "../../../../utils/Custom/ListSearchSection";
import ListView from "../../../../utils/Custom/listView";
import { getPermissions } from "../../../Pages/rbac/hasAccess";
const headers = [
  "id",
  "pipelineId",
  "candidateName",
  "positionTitle",
  "client",
  "pipelineStage",
  "round",
  "mode",
  "overallScore",
  "interviewer",
  "stageStatus",
];

const fields = [
  { value: "pipelineId", label: "Pipeline ID" },
  { value: "candidateName", label: "Candidate Name" },
  { value: "positionTitle", label: "Position Title" },
  { value: "pipelineStage", label: "Pipeline Stage" },
  { value: "stageStatus", label: "Stage Status" },
  { value: "mode", label: "Interview Mode" },
];

const PipelineList = (props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const permissions = getPermissions(props.moduleId);

  const handleEdit = (row) => navigate(`/talent-acquisition/editPipeline/${row.id}`);
  const handleDelete = (row) => submitSiteDelete("talentAcqPipeline", row.id);

  const [pipelineList, setPipelineList] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [searchBy, setSearchBy] = React.useState(sessionStorage.getItem("searchBy") || "candidateName");
  const [searchKey, setSearchKey] = React.useState(sessionStorage.getItem("searchKey") || "");
  const [searched, setSearched] = React.useState(sessionStorage.getItem("searched") === "true" || false);
  const [currentPage, setCurrentPage] = useState(() => Number(sessionStorage.getItem("ListPageCount") || 0));
  const [rowsPerPage, setRowsPerPage] = useState(() => Number(sessionStorage.getItem("ListRowsPerPage") || 30));

  const [stageMaster, setStageMaster] = useState([]);
  const [stageStatusMaster, setStageStatusMaster] = useState([]);
  const [modeMaster, setModeMaster] = useState([]);

  useEffect(() => {
    const loadMasters = async () => {
      try {
        const records = await fetchQuery(
          "SELECT id, type, Value FROM talentAcquisitionMaster WHERE type IN (5, 6, 7) AND status = 1"
        );
        if (records && records.length > 0) {
          setStageMaster(records.filter((m) => m.type == 5).map((m) => ({ id: m.id, name: m.Value })));
          setStageStatusMaster(records.filter((m) => m.type == 6).map((m) => ({ id: m.id, name: m.Value })));
          setModeMaster(records.filter((m) => m.type == 7).map((m) => ({ id: m.id, name: m.Value })));
        }
      } catch (e) {
        console.error("Error loading pipeline masters:", e);
      }
    };
    loadMasters();
  }, []);

  const handlePaginationChange = (page, rows) => {
    setCurrentPage(page);
    setRowsPerPage(rows);
    sessionStorage.setItem("currentPath", location.pathname);
    sessionStorage.setItem("ListPageCount", page);
    sessionStorage.setItem("ListRowsPerPage", rows);
  };

  const getWhereClause = (tablePrefix = "") => {
    const p = tablePrefix ? `${tablePrefix}.` : "";
    const key = searchKey.trim();
    let whereConditions = [`${p}status = 1`];

    if (searchBy && key) {
      if (searchBy === "candidateName") {
        whereConditions.push(
          `c.candidateName LIKE '%${key}%'`
        );
      } else if (searchBy === "positionTitle") {
        whereConditions.push(
          `r.positionTitle LIKE '%${key}%'`
        );
      } else if (searchBy === "pipelineStage") {
        const found = stageMaster.filter(m => m.name.toLowerCase().includes(key.toLowerCase())).map(m => m.id);
        if (found.length > 0) {
          whereConditions.push(`${p}stage IN (${found.join(",")})`);
        } else {
          whereConditions.push("1 = 0");
        }
      } else if (searchBy === "stageStatus") {
        const found = stageStatusMaster.filter(m => m.name.toLowerCase().includes(key.toLowerCase())).map(m => m.id);
        if (found.length > 0) {
          whereConditions.push(`${p}stageStatus IN (${found.join(",")})`);
        } else {
          whereConditions.push("1 = 0");
        }
      } else if (searchBy === "mode") {
        const found = modeMaster.filter(m => m.name.toLowerCase().includes(key.toLowerCase())).map(m => m.id);
        if (found.length > 0) {
          whereConditions.push(`${p}mode IN (${found.join(",")})`);
        } else {
          whereConditions.push("1 = 0");
        }
      } else {
        whereConditions.push(`${p}${searchBy} LIKE '%${key}%'`);
      }
    }

    return `WHERE ${whereConditions.join(" AND ")}`;
  };

  const getDataQuery = () => {
    const whereClause = getWhereClause("p");
    const pagination =
      rowsPerPage !== -1
        ? `LIMIT ${rowsPerPage} OFFSET ${rowsPerPage * currentPage}`
        : "";
    return `SELECT p.*, c.candidateName, r.positionTitle 
            FROM talentAcqPipeline p 
            LEFT JOIN talentAcqCandidate c ON p.candId = c.id 
            LEFT JOIN talentAcqRequirement r ON p.requirementId = r.id 
            ${whereClause} ORDER BY p.id DESC ${pagination};`;
  };

  const getCountQuery = () => {
    return `SELECT COUNT(*) AS totalRows 
            FROM talentAcqPipeline p 
            LEFT JOIN talentAcqCandidate c ON p.candId = c.id 
            LEFT JOIN talentAcqRequirement r ON p.requirementId = r.id 
            ${getWhereClause("p")};`;
  };

  const fetchData = async () => {
    try {
      const [data, countResult] = await Promise.all([
        fetchQuery(getDataQuery()),
        fetchQuery(getCountQuery()),
      ]);

      const mapped = (data || []).map((row) => ({
        ...row,
        candidateName: row.candidateName || row.candId || "",
        positionTitle: row.positionTitle || row.requirementId || "",
        pipelineStage: stageMaster.find((m) => m.id == row.stage)?.name || row.stage || "",
        stageStatus: stageStatusMaster.find((m) => m.id == row.stageStatus)?.name || row.stageStatus || "",
        mode: modeMaster.find((m) => m.id == row.mode)?.name || row.mode || "",
      }));

      setPipelineList(mapped);
      setTotalRows(countResult?.[0]?.totalRows || 0);
    } catch (error) {
      console.error("Error fetching pipeline data:", error);
    }
  };

  const handleSearch = () => {
    if (!searchBy || !searchKey.trim()) {
      alert("Please select a search field and enter a search value.");
      return;
    }
    fetchData();
    setSearched(true);
    sessionStorage.setItem("searchBy", searchBy);
    sessionStorage.setItem("searchKey", searchKey);
    sessionStorage.setItem("searched", "true");
    sessionStorage.setItem("currentPath", location.pathname);
    sessionStorage.setItem("ListPageCount", 0);
    setCurrentPage(0);
  };

  const clearSearch = () => {
    setSearched(false);
    setSearchBy("candidateName");
    setSearchKey("");
    sessionStorage.removeItem("ListPageCount");
    sessionStorage.removeItem("ListRowsPerPage");
    sessionStorage.removeItem("searchBy");
    sessionStorage.removeItem("searchKey");
    sessionStorage.removeItem("searched");
    setCurrentPage(0);
  };

  useEffect(() => {
    fetchData();
  }, [currentPage, rowsPerPage]);

  useEffect(() => {
    if (searched === false) {
      fetchData();
    }
  }, [searched]);

  return (
    <>
      {(permissions.create || permissions.allAccess) && (
        <AddButton
          id="btn-add-pipeline"
          to="/talent-acquisition/editPipeline/0"
          label="Add Pipeline"
          icon={<AddIcon fontSize="small" />}
        />
      )}
      <ListSearchSection
        title="Search Pipeline"
        fields={fields}
        onSearch={handleSearch}
        searchKey={searchKey}
        setSearchKey={setSearchKey}
        searchBy={searchBy}
        setSearchBy={setSearchBy}
        clearSearch={clearSearch}
        searched={searched}
        setSearched={setSearched}
      />
      <ListView
        headers={headers}
        data={pipelineList}
        actions={{
          edit: handleEdit,
          delete: handleDelete,
        }}
        showCheckbox={true}
        deleteTableName="talentAcqPipeline"
        moduleId={props.moduleId}
        currentPage={currentPage}
        rowsPerPage={rowsPerPage}
        totalRows={totalRows}
        onPaginationChange={handlePaginationChange}
      />
    </>
  );
};

export default PipelineList;
